import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { useApolloClient } from '@apollo/client';

import { sum } from 'utils/calculator';
import { isValidInternationalPhoneNumber, pnrRegex } from 'utils/validators';
import AgentPortalError from 'errors/AgentPortalError';

import { GET_BOOKING } from 'store/queries';
import { stationsShape } from 'constants/propTypes';
import Select from 'components/ui/Select';
import { Row, Column } from 'components/shared/Layout';
import StationSelectField from 'components/ui/StationSelectField';
import Button, { ConfirmButton, CancelButton } from 'components/ui/Button';
import CurrencySelect from 'components/shared/CurrencySelect';

import TextInput from 'components/ui/TextInput';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import LuggageExtrasSection from './LuggageExtrasSection';

import {
  StyledHeading,
  StyledPage,
  StyledLabel,
  ClearButton,
  StyledH2,
  StyledPriceLabel,
  FormContainer,
  ErrorMessage,
  StyledCurrencyInput,
  TotalBar,
  BoldLabel,
  StyledBookingReferenceInput,
  StyledPassengerDetails,
  StyledExtraDetail,
  StyledStationRow,
  StyledExtraNameField,
  StyledPriceField,
  StyledDateField,
  StyledStationSelect,
  AssociatedPnrSection,
  ExtraDetailsContainer,
  EnterButton,
  StyledDropdown,
  StyledQuantityField,
  TotalPriceDisplay
} from './ExtrasOnlyBooking.styles';

const extraSelectedDetails = ({ availableExtras = [], extraName = '' }) =>
  availableExtras.find(({ tariffCode }) => tariffCode === extraName);

const FIXED_PRICED_PRODUCTS = ['SU', 'DG'];

const ExtrasOnlyBooking = ({ availableExtras, stations, onSubmit, currency, updateCurrency, intl }) => {
  const client = useApolloClient();
  const [pnr, setPnr] = useState('');
  const [booking, setBooking] = useState(null);
  const [isBookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState(null);
  const [selectedPassenger, setSelectedPassenger] = useState('');

  const selectPassengerText = intl.formatMessage({ id: 'addAssistance.selectPassenger' });

  const isPnrValid = !pnr || pnrRegex.test(pnr);
  const passengers = booking?.passengers?.filter((p) => p.firstName && p.lastName);

  const [luggageExtrasState, setLuggageExtrasState] = useState({
    formPayload: { add: [], update: [], remove: [], removedDetails: [] },
    isAssistanceIncomplete: false
  });

  const defaultValues = {
    extras: []
  };
  const { register, handleSubmit, control, formState, watch, reset, setValue, getValues } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues
  });

  const { errors } = formState;

  const { fields, append, remove } = useFieldArray({ control, name: 'extras' });

  const extras = watch('extras');

  const hasExtras = Boolean(extras);
  const extrasTotal = hasExtras && sum(extras.map((extra) => extra.price * extra.quantity));

  const luggageExtrasTotal = React.useMemo(() => {
    const { formPayload, transformedLuggageExtras } = luggageExtrasState;
    if (!formPayload || !formPayload.add || !transformedLuggageExtras) {
      return 0;
    }

    const allExtras = [...(transformedLuggageExtras.outbound || []), ...(transformedLuggageExtras.inbound || [])];
    let total = 0;

    formPayload.add.forEach((addItem) => {
      const extra = allExtras.find((e) => e.id === addItem.id);
      if (extra) {
        total += addItem.items.length * (extra.price || 0);
      }
    });

    return total;
  }, [luggageExtrasState]);

  const total = (extrasTotal || 0) + luggageExtrasTotal;

  const hasSelectedExtras =
    extras.length > 0 ||
    luggageExtrasState.formPayload.add.length > 0 ||
    (luggageExtrasState.formPayload.update?.length ?? 0) > 0 ||
    luggageExtrasState.formPayload.remove.length > 0;

  const resetFormData = () => {
    setPnr('');
    setBooking(null);
    reset(defaultValues);
  };

  const submit = (values) =>
    onSubmit({
      ...values,
      associatedBookingReference: booking.reference,
      luggageExtras: luggageExtrasState,
      selectedPassenger,
      passengers: booking?.passengers || []
    });

  const loadAssociatedBooking = async (e) => {
    e.preventDefault();
    reset(defaultValues);
    try {
      setBookingLoading(true);
      const { data } = await client.query({
        query: GET_BOOKING,
        variables: {
          reference: pnr
        },
        fetchPolicy: 'network-only'
      });
      for (let i = 0; i < fields.length; i += 1) {
        setValue(`extras.${i}.origin`, data.booking?.eurostarOutbound?.[0]?.origin?.uic || '');
        setValue(`extras.${i}.destination`, data.booking?.eurostarOutbound?.[0]?.destination?.uic || '');
      }
      setBooking(data.booking);
    } catch (err) {
      setBookingError(err);
    } finally {
      setBookingLoading(false);
    }
  };

  const addExtra = () => {
    append({ name: 'extras', passengerId: '' });
  };

  const swapOriginDestination = (index) => {
    const destinationValue = extras[index].destination;
    const originValue = extras[index].origin;
    setValue(`extras.${index}.origin`, destinationValue);
    setValue(`extras.${index}.destination`, originValue);
  };

  useEffect(() => {
    if (!booking) {
      return;
    }
    const values = getValues();
    if (values.extras.length === 0) {
      return;
    }
    const lastExtra = values.extras[values.extras.length - 1];
    if (!lastExtra.origin && !lastExtra.destination) {
      setValue(`extras.${values.extras.length - 1}.origin`, booking?.eurostarOutbound?.[0]?.origin?.uic || '');
      setValue(`extras.${values.extras.length - 1}.destination`, booking?.eurostarOutbound?.[0]?.destination?.uic || '');
    }
  }, [booking, fields.length, getValues, setValue]);

  useEffect(() => {
    const availablePassengers = booking?.passengers?.filter((p) => p.firstName && p.lastName);
    if (!availablePassengers?.length) {
      return;
    }
    const passenger = availablePassengers.find((p) => p.id === selectedPassenger) || availablePassengers[0];
    setValue('firstName', passenger.firstName);
    setValue('lastName', passenger.lastName);
    setValue('email', passenger.email || '');
    setValue(
      'phone',
      `${passenger.unformattedPhoneNumber?.countryCode?.replace('+', '') ?? ''}${passenger.unformattedPhoneNumber?.number ?? ''}`
    );
  }, [booking, selectedPassenger, setValue]);

  if (bookingError) {
    throw new AgentPortalError(bookingError);
  }

  return (
    <StyledPage>
      <CurrencySelect defaultValue={currency} onSelect={updateCurrency} />
      <StyledHeading>
        <FormattedMessage id="extrasOnlyBookings.title" />
        <ClearButton onClick={resetFormData}>
          <FormattedMessage id="extrasOnlyBookings.clearForm" />
        </ClearButton>
      </StyledHeading>
      <AssociatedPnrSection data-testid="associated-pnr-form" onSubmit={loadAssociatedBooking}>
        <StyledLabel htmlFor="associatedBookingReference">
          <BoldLabel>
            <FormattedMessage id="extrasOnlyBookings.fields.pnr" />
          </BoldLabel>
          <StyledBookingReferenceInput
            name="associatedBookingReference"
            type="text"
            value={pnr}
            onChange={(e) => setPnr(e.target.value)}
            placeholder="4YGHYK"
            data-testid="associated-pnr-input"
          />
          <EnterButton id="associated-pnr-submit" type="submit" disabled={!pnr || !isPnrValid}>
            <FormattedMessage id="paxForm.enter" />
          </EnterButton>
          {!isPnrValid && (
            <ErrorMessage>
              <FormattedMessage id="extrasOnlyBookings.errors.pnr" />
            </ErrorMessage>
          )}
        </StyledLabel>
      </AssociatedPnrSection>
      <form data-testid="extras-details-form" onSubmit={handleSubmit(submit)}>
        <ExtraDetailsContainer isDisabled={isBookingLoading || !booking}>
          <StyledPassengerDetails>
            <StyledH2>
              <FormattedMessage id="extrasOnlyBookings.passengerDetails" />
            </StyledH2>
          </StyledPassengerDetails>
          <FormContainer>
            {passengers?.length > 0 && (
              <div>
                <StyledDropdown
                  name="passengers"
                  value={selectedPassenger}
                  data-cy="passengers-dropdown"
                  onChange={(e) => setSelectedPassenger(e.target.value)}
                >
                  {passengers.map((passenger) => (
                    <option key={passenger.id} value={passenger.id}>
                      {`${passenger.firstName} ${passenger.lastName}`}
                    </option>
                  ))}
                </StyledDropdown>
              </div>
            )}
            <Row>
              <Column span={4}>
                <StyledLabel htmlFor="firstName">
                  <FormattedMessage id="extrasOnlyBookings.fields.firstName" />
                </StyledLabel>
                <TextInput
                  type="text"
                  {...register('firstName', {
                    required: true,
                    pattern: /^([\D]|-){2,34}$/
                  })}
                />
                {errors.firstName && (
                  <ErrorMessage>
                    <FormattedMessage id="extrasOnlyBookings.errors.firstName" />
                  </ErrorMessage>
                )}
              </Column>
              <Column span={4}>
                <StyledLabel htmlFor="lastName">
                  <FormattedMessage id="extrasOnlyBookings.fields.lastName" />
                </StyledLabel>
                <TextInput
                  type="text"
                  {...register('lastName', {
                    required: true,
                    pattern: /^([\D]|-){2,34}$/
                  })}
                />
                {errors.lastName && (
                  <ErrorMessage>
                    <FormattedMessage id="extrasOnlyBookings.errors.lastName" />
                  </ErrorMessage>
                )}
              </Column>
              <Column span={4}>
                <StyledLabel htmlFor="email">
                  <FormattedMessage id="extrasOnlyBookings.fields.email" />
                </StyledLabel>
                <TextInput
                  type="text"
                  {...register('email', {
                    required: true,
                    pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                    minLength: 6,
                    maxLength: 50
                  })}
                />
                {errors.email && (
                  <ErrorMessage>
                    <FormattedMessage id="extrasOnlyBookings.errors.email" />
                  </ErrorMessage>
                )}
              </Column>
              <Column span={4}>
                <StyledLabel htmlFor="phone">
                  <FormattedMessage id="extrasOnlyBookings.fields.phone" />
                </StyledLabel>
                <TextInput
                  type="text"
                  {...register('phone', {
                    required: true,
                    validate: isValidInternationalPhoneNumber
                  })}
                />
                {errors.phone && (
                  <ErrorMessage>
                    <FormattedMessage id="extrasOnlyBookings.errors.phone" />
                  </ErrorMessage>
                )}
              </Column>
            </Row>
          </FormContainer>

          {booking?.reference && (
            <>
              <StyledH2>
                <FormattedMessage id="extrasOnlyBookings.extras" />
              </StyledH2>
              {fields.map((field, index) => {
                const getError = (key) => errors?.extras?.[index]?.[key];
                const getValue = (key) => getValues()?.extras?.[index]?.[key];
                const extraSelected = extraSelectedDetails({ availableExtras, extraName: getValue('extraName') });

                const hasFixedPrice = FIXED_PRICED_PRODUCTS.includes(extraSelected?.type);

                if (hasFixedPrice && extraSelected?.price && extraSelected.price !== getValue('price')) {
                  setValue(`extras.${index}.price`, extraSelected?.price);
                }

                return (
                  <FormContainer key={field.id}>
                    <StyledExtraDetail>
                      <StyledExtraNameField>
                        <StyledLabel htmlFor={`extras.${index}.passengerId`}>
                          <FormattedMessage id="addAssistance.passenger" />
                        </StyledLabel>
                        <Select
                          {...register(`extras.${index}.passengerId`, {
                            required: true
                          })}
                          data-cy={`extra-passenger-${index}`}
                        >
                          <option disabled value="">
                            {selectPassengerText}
                          </option>
                          {passengers?.map(({ id, firstName, lastName }) => (
                            <option key={id} value={id}>
                              {`${firstName} ${lastName}`}
                            </option>
                          ))}
                        </Select>
                        {getError('passengerId') && (
                          <ErrorMessage>
                            <FormattedMessage id="addAssistance.incompleteWarning" />
                          </ErrorMessage>
                        )}
                      </StyledExtraNameField>
                      <StyledExtraNameField>
                        <StyledLabel htmlFor="extraName">
                          <FormattedMessage id="extrasOnlyBookings.fields.extra" />
                        </StyledLabel>
                        <Select
                          {...register(`extras.${index}.extraName`, {
                            required: true
                          })}
                        >
                          {availableExtras.map(({ tariffCode, name }) => (
                            <option key={tariffCode} value={tariffCode}>
                              {name}
                            </option>
                          ))}
                        </Select>
                      </StyledExtraNameField>
                      <StyledQuantityField>
                        <StyledPriceLabel htmlFor="quantity">
                          <FormattedMessage id="extrasOnlyBookings.fields.quantity" />
                        </StyledPriceLabel>

                        <TextInput
                          data-cy="extra-quantity"
                          data-id="extra-quantity"
                          type="number"
                          {...register(`extras.${index}.quantity`, {
                            required: true,
                            min: 0,
                            max: 20
                          })}
                          id={`extras.${index}.quantity`}
                          step="1"
                          placeholder="1"
                        />
                        {getError('quantity') && (
                          <ErrorMessage>
                            <FormattedMessage id="extrasOnlyBookings.errors.quantity" />
                          </ErrorMessage>
                        )}
                      </StyledQuantityField>
                      <StyledPriceField>
                        <StyledPriceLabel htmlFor="price">
                          <FormattedMessage id="extrasOnlyBookings.fields.price" />
                        </StyledPriceLabel>
                        <StyledCurrencyInput
                          currency={currency}
                          isControlled={false}
                          {...register(`extras.${index}.price`, {
                            required: true,
                            min: 0
                          })}
                          step={0.01}
                          {...(hasFixedPrice ? { readOnly: true } : {})}
                        />
                        {getError('price') && (
                          <ErrorMessage>
                            <FormattedMessage id="extrasOnlyBookings.errors.price" />
                          </ErrorMessage>
                        )}
                      </StyledPriceField>
                      <StyledDateField>
                        <StyledLabel htmlFor="total">
                          <FormattedMessage id="extrasOnlyBookings.fields.total" />
                        </StyledLabel>
                        <TotalPriceDisplay>
                          <FormattedCurrency amount={getValue('quantity') * getValue('price')} currency={currency} />
                        </TotalPriceDisplay>
                      </StyledDateField>
                      <StyledDateField>
                        <StyledLabel htmlFor="date">
                          <FormattedMessage id="extrasOnlyBookings.fields.date" />
                        </StyledLabel>
                        <TextInput type="date" {...register(`extras.${index}.date`, { required: true })} />
                        {getError('date') && (
                          <ErrorMessage>
                            <FormattedMessage id="extrasOnlyBookings.errors.date" />
                          </ErrorMessage>
                        )}
                      </StyledDateField>
                    </StyledExtraDetail>
                    <StyledStationRow>
                      <StyledStationSelect>
                        <Controller
                          rules={{ required: true }}
                          name={`extras.${index}.origin`}
                          control={control}
                          render={({ field }) => (
                            <StationSelectField id={`extras.${index}.origin`} stations={stations} label="Origin" {...field} />
                          )}
                        />
                      </StyledStationSelect>
                      <Button onClick={() => swapOriginDestination(index)} type="button" label="↔" id="swap-button" />
                      <StyledStationSelect>
                        <Controller
                          rules={{ required: true }}
                          name={`extras.${index}.destination`}
                          control={control}
                          render={({ field }) => (
                            <StationSelectField id={`extras.${index}.destination`} stations={stations} label="Destination" {...field} />
                          )}
                        />
                      </StyledStationSelect>
                    </StyledStationRow>
                    {index !== 0 && (
                      <CancelButton data-cy={`remove-extra-${index}`} onClick={() => remove(index)}>
                        <FormattedMessage id="extrasOnlyBookings.removeExtra" />
                      </CancelButton>
                    )}
                  </FormContainer>
                );
              })}

              <Button type="button" onClick={addExtra} data-cy="add-extra-button">
                <FormattedMessage id="extrasOnlyBookings.addNewExtra" />
              </Button>

              <LuggageExtrasSection
                key={booking?.reference}
                currency={currency}
                booking={booking}
                passengers={passengers || []}
                onStateChange={setLuggageExtrasState}
              />
            </>
          )}

          {booking?.reference && (
            <TotalBar>
              <FormattedMessage id="extrasOnlyBookings.total" /> <FormattedCurrency amount={total} currency={currency} />
            </TotalBar>
          )}

          <ConfirmButton
            type="submit"
            data-cy="extras-form-submit"
            disabled={!hasSelectedExtras || luggageExtrasState.isAssistanceIncomplete}
          >
            {total > 0 ? <FormattedMessage id="extrasOnlyBookings.submitPaid" /> : <FormattedMessage id="extrasOnlyBookings.submitFree" />}
          </ConfirmButton>
        </ExtraDetailsContainer>
      </form>
    </StyledPage>
  );
};

ExtrasOnlyBooking.propTypes = {
  intl: intlShape.isRequired,
  currency: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  availableExtras: PropTypes.arrayOf(
    PropTypes.shape({
      tariffCode: PropTypes.string,
      name: PropTypes.string
    })
  ).isRequired,
  stations: stationsShape.isRequired,
  updateCurrency: PropTypes.func.isRequired
};

export default injectIntl(ExtrasOnlyBooking);
